import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ComposedChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, ReferenceArea, ReferenceLine,
} from "recharts";
import { api } from "@/lib/api";

/**
 * MTF Impulse — Historical Replay panel.
 *
 * Split into small sub-components (all in this file to keep the feature
 * co-located):
 *   • BackfillCard / ReplayForm — controls
 *   • RunsList — history table
 *   • SummaryCards / EquityChart / PriceRegimeChart / TradesTable — results
 *
 * All heavy work happens on the backend; the UI is a thin observer.
 */

// ---------------------------------------------------------------------------
// Static style / config constants — hoisted so React sees stable references
// and doesn't re-render every child on each parent render.
// ---------------------------------------------------------------------------
const AXIS_TICK = { fontSize: 10, fill: "#94a3b8" };
const AXIS_TICK_SMALL = { fontSize: 9, fill: "#94a3b8" };
const CHART_MARGIN = { top: 5, right: 10, left: 0, bottom: 0 };
const TOOLTIP_STYLE = { background: "#0f172a", border: "1px solid #334155", fontSize: 11 };
const REF_LINE_LABEL = { value: "Initial ₹1L", fill: "#64748b", fontSize: 10, position: "insideTopRight" };
const EQUITY_Y_DOMAIN = ["dataMin - 500", "dataMax + 500"];
const PRICE_Y_DOMAIN = ["auto", "auto"];
const REGIME_BULL_COLOR = "#22c55e";
const REGIME_BEAR_COLOR = "#ef4444";
const REGIME_FILL_OPACITY = 0.06;
const ENTRY_MARKER_LABEL = { value: "IN", fill: "#22c55e", fontSize: 9 };
const EXIT_MARKER_LABEL_BASE = { fill: "#94a3b8", fontSize: 9 };

// ---------------------------------------------------------------------------
// Small formatting helpers — no hooks, no state, safe to reuse.
// ---------------------------------------------------------------------------
function pctFmt(v) { return (Number(v || 0) * 100).toFixed(1) + "%"; }
function inrFmt(v, d = 2) { const n = Number(v || 0); return "₹" + n.toFixed(d); }
function tsLabel(s) { return s ? String(s).slice(11, 16) : "—"; }
function exitCode(r) { return r ? r.replace("CLOSED_", "").toLowerCase() : "—"; }

function runStateTone(state) {
  if (state === "COMPLETED") return "text-pos";
  if (state === "FAILED") return "text-red-400";
  if (state === "RUNNING") return "text-warn";
  return "text-slate-500";
}
function summaryTone(state) {
  if (state === "COMPLETED") return "pos";
  if (state === "FAILED") return "neg";
  return "warn";
}
function exitReasonTone(reason) {
  if (reason === "CLOSED_TARGET") return "text-pos";
  if (reason === "CLOSED_STOP") return "text-neg";
  return "text-warn";
}
function exitReasonStroke(reason) {
  if (reason === "CLOSED_TARGET") return "#22c55e";
  if (reason === "CLOSED_STOP") return "#ef4444";
  return "#f59e0b";
}
function pnlTone(v) { return Number(v || 0) >= 0 ? "text-pos" : "text-neg"; }
function defaultFromDate() { const d = new Date(); d.setDate(d.getDate() - 20); return d.toISOString().slice(0, 10); }
function defaultToDate() { const d = new Date(); d.setDate(d.getDate() - 1); return d.toISOString().slice(0, 10); }
function tradeKey(t, idx) {
  // Prefer the deterministic replay signal id; fall back to entry_ts+idx
  // when the row is a placeholder without one (never expected at render).
  return t.source_signal_id || `${t.entry_ts || ""}-${idx}`;
}
function regimeKey(r) { return `${r.start}-${r.end}`; }
function tradeMarkerKey(t) {
  return t.source_signal_id || `${t.entry_ts || ""}-${t.exit_ts || ""}`;
}


// ===========================================================================
// Root component
// ===========================================================================
export default function MtfReplayPanel() {
  const [runs, setRuns] = useState([]);
  const [activeRun, setActiveRun] = useState(null);
  const [runDetail, setRunDetail] = useState(null);
  const [trades, setTrades] = useState([]);
  const [candles, setCandles] = useState(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadRuns = useCallback(async () => {
    try {
      const r = await api.get("/mtf/replay/runs?limit=25");
      setRuns(r.data.items || []);
    } catch (err) {
      // Non-fatal on the polling path; surface via console for observability.
      console.error("mtf.loadRuns failed", err);
    }
  }, []);

  useEffect(() => {
    loadRuns();
    const t = setInterval(loadRuns, 5000);
    return () => clearInterval(t);
  }, [loadRuns]);

  // Note: loadRun does NOT depend on selectedDate — it seeds it via the
  // functional setter, so the dependency array can stay empty and this
  // callback keeps a stable identity across polls.
  const loadRun = useCallback(async (runId) => {
    if (!runId) return;
    try {
      const [detailRes, tradesRes] = await Promise.all([
        api.get(`/mtf/replay/runs/${runId}`),
        api.get(`/mtf/replay/runs/${runId}/trades`),
      ]);
      setRunDetail(detailRes.data);
      setTrades(tradesRes.data.items || []);
      const eq = detailRes.data?.equity || [];
      // Default candle-chart date = last equity day, but only if the user
      // hasn't already picked one.
      setSelectedDate((prev) => prev || (eq.length ? eq[eq.length - 1].trading_date : ""));
    } catch (err) {
      setMsg({ level: "error", text: err?.response?.data?.detail || err.message });
    }
  }, []);

  useEffect(() => { if (activeRun) loadRun(activeRun); }, [activeRun, loadRun]);

  // Auto-refresh detail while the run is RUNNING.
  const runState = runDetail?.run?.state;
  useEffect(() => {
    if (!activeRun) return;
    if (runState !== "QUEUED" && runState !== "RUNNING") return;
    const t = setInterval(() => loadRun(activeRun), 3000);
    return () => clearInterval(t);
  }, [activeRun, runState, loadRun]);

  const loadCandles = useCallback(async () => {
    if (!activeRun || !selectedDate) return;
    try {
      const r = await api.get(`/mtf/replay/runs/${activeRun}/candles?date=${selectedDate}`);
      setCandles(r.data);
    } catch (err) {
      console.error("mtf.loadCandles failed", err);
      setCandles(null);
    }
  }, [activeRun, selectedDate]);

  useEffect(() => { loadCandles(); }, [loadCandles]);

  const handleSelectRun = useCallback((runId) => {
    setActiveRun(runId);
    setSelectedDate("");
  }, []);

  const summary = runDetail?.run || {};
  const equity = runDetail?.equity || [];

  return (
    <div className="space-y-6" data-testid="mtf-replay-panel">
      <ControlsSection
        busy={busy} setBusy={setBusy}
        msg={msg} setMsg={setMsg}
        onReplayStarted={(runId) => { setActiveRun(runId); loadRuns(); }}
      />

      <RunsList runs={runs} activeRun={activeRun} onSelect={handleSelectRun} />

      {activeRun && summary?.run_id ? (
        <RunResults
          summary={summary} equity={equity} trades={trades}
          candles={candles} selectedDate={selectedDate}
          onSelectDate={setSelectedDate}
        />
      ) : null}
    </div>
  );
}


// ===========================================================================
// Controls (Backfill + Replay start)
// ===========================================================================
function ControlsSection({ busy, setBusy, msg, setMsg, onReplayStarted }) {
  const [backfillDays, setBackfillDays] = useState(20);
  const [fromDate, setFromDate] = useState(defaultFromDate());
  const [toDate, setToDate] = useState(defaultToDate());
  const [label, setLabel] = useState("");

  const runBackfill = async () => {
    const q = `Fetch ${backfillDays} trading days of 1-min NIFTY + NIFTYBEES from Kite historical_data? Runs in background; poll status here.`;
    if (!window.confirm(q)) return null;
    setBusy(true); setMsg(null);
    try {
      const r = await api.post("/mtf/backfill", { days: backfillDays });
      setMsg({ level: "ok",
        text: `Backfill queued — job ${r.data.job_id.slice(-8)}` });
      return r.data;
    } catch (err) {
      setMsg({ level: "error", text: err?.response?.data?.detail || err.message });
      return null;
    } finally { setBusy(false); }
  };

  const startReplay = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await api.post("/mtf/replay",
        { from_date: fromDate, to_date: toDate, label });
      setMsg({ level: "ok", text: `Replay ${r.data.run_id} queued` });
      onReplayStarted(r.data.run_id);
    } catch (err) {
      setMsg({ level: "error", text: err?.response?.data?.detail || err.message });
    } finally { setBusy(false); }
  };

  return (
    <div className="surface rounded-sm p-4">
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">
        Historical Replay (Phase D)
      </div>
      <p className="text-xs text-slate-400 mb-3">
        Backtest the MTF Impulse strategy against real 1-min Kite candles.
        Requires Zerodha to be CONNECTED (backfill hits <code>historical_data</code>).
        Options branch is skipped (no historical option chain) —
        NIFTYBEES book is fully simulated with round-trip charges.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <BackfillCard days={backfillDays} setDays={setBackfillDays}
                       busy={busy} onRun={runBackfill} />
        <ReplayForm from={fromDate} setFrom={setFromDate}
                     to={toDate} setTo={setToDate}
                     label={label} setLabel={setLabel}
                     busy={busy} onStart={startReplay} />
      </div>
      {msg ? (
        <div className={`text-xs font-mono mt-3 ${msg.level === "ok" ? "text-pos" : "text-red-400"}`}
             data-testid="mtf-replay-msg">
          {msg.text}
        </div>
      ) : null}
    </div>
  );
}

function BackfillCard({ days, setDays, busy, onRun }) {
  const [status, setStatus] = useState(null);
  const [jobId, setJobId] = useState(null);

  useEffect(() => {
    if (!jobId || (status && ["COMPLETED", "FAILED"].includes(status.state))) return;
    let alive = true;
    const tick = async () => {
      try {
        const r = await api.get(`/mtf/backfill/status/${jobId}`);
        if (alive) setStatus(r.data);
      } catch (err) { console.error("backfill status poll failed", err); }
    };
    tick();
    const t = setInterval(tick, 2000);
    return () => { alive = false; clearInterval(t); };
  }, [jobId, status?.state]);

  const handleRun = async () => {
    const started = await onRun();
    if (started?.job_id) { setJobId(started.job_id); setStatus(null); }
  };

  return (
    <div className="border border-slate-800 rounded-sm p-3">
      <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-2">
        1. Backfill 1-min candles
      </div>
      <div className="flex items-center gap-2">
        <input type="number" min={1} max={60} value={days}
               onChange={(e) => setDays(Math.max(1, Math.min(60, Number(e.target.value) || 1)))}
               className="w-16 bg-slate-950 border border-slate-800 text-slate-100 text-xs font-mono px-2 py-1 rounded-sm"
               data-testid="mtf-backfill-days-input" />
        <span className="text-[11px] text-slate-500">days</span>
        <button onClick={handleRun} disabled={busy || (status && status.state === "RUNNING")}
                data-testid="mtf-backfill-btn"
                className="text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 rounded-sm bg-slate-800 hover:bg-purple-700 disabled:opacity-40 text-slate-200">
          Fetch from Kite
        </button>
      </div>
      {status ? (
        <div className="mt-2 text-[10px] font-mono" data-testid="mtf-backfill-status">
          <span className={status.state === "COMPLETED" ? "text-pos"
                            : status.state === "FAILED" ? "text-red-400"
                            : "text-warn"}>
            {status.state}
          </span>
          {" "}· {status.days_processed || 0}/{status.days || 0} days
          {" "}· NIFTY {status.nifty_inserted || 0}
          {" "}· BEES {status.bees_inserted || 0}
          {status.error ? (
            <div className="text-red-400 mt-1 break-all">{status.error}</div>
          ) : null}
        </div>
      ) : (
        <p className="text-[10px] text-slate-600 mt-2">
          Idempotent — safe to re-run. Skips weekends automatically.
        </p>
      )}
    </div>
  );
}

function ReplayForm({ from, setFrom, to, setTo, label, setLabel, busy, onStart }) {
  return (
    <div className="border border-slate-800 rounded-sm p-3">
      <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-2">
        2. Run Replay
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className="text-[9px] text-slate-600 uppercase mb-1">From</div>
          <input type="date" value={from}
                 onChange={(e) => setFrom(e.target.value)}
                 className="w-full bg-slate-950 border border-slate-800 text-slate-100 text-xs font-mono px-2 py-1 rounded-sm"
                 data-testid="mtf-replay-from" />
        </div>
        <div>
          <div className="text-[9px] text-slate-600 uppercase mb-1">To</div>
          <input type="date" value={to}
                 onChange={(e) => setTo(e.target.value)}
                 className="w-full bg-slate-950 border border-slate-800 text-slate-100 text-xs font-mono px-2 py-1 rounded-sm"
                 data-testid="mtf-replay-to" />
        </div>
        <div className="col-span-2">
          <div className="text-[9px] text-slate-600 uppercase mb-1">Label (optional)</div>
          <input type="text" value={label}
                 onChange={(e) => setLabel(e.target.value)}
                 placeholder="e.g. baseline v1"
                 className="w-full bg-slate-950 border border-slate-800 text-slate-100 text-xs font-mono px-2 py-1 rounded-sm"
                 data-testid="mtf-replay-label" />
        </div>
      </div>
      <button onClick={onStart} disabled={busy}
              data-testid="mtf-replay-start-btn"
              className="mt-3 w-full text-[10px] uppercase tracking-[0.15em] px-3 py-2 rounded-sm bg-purple-700 hover:bg-purple-600 disabled:opacity-40 text-slate-100">
        Start Replay
      </button>
    </div>
  );
}


// ===========================================================================
// Runs list
// ===========================================================================
function RunsList({ runs, activeRun, onSelect }) {
  return (
    <div className="surface rounded-sm p-4" data-testid="mtf-runs-list">
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">
        Replay Runs
      </div>
      {runs.length === 0 ? (
        <p className="text-xs text-slate-500 py-4">No runs yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[11px] font-mono">
            <thead className="text-[9px] uppercase tracking-[0.15em] text-slate-600">
              <tr>
                <th className="text-left py-1">Run</th>
                <th className="text-left">Label</th>
                <th className="text-left">Range</th>
                <th className="text-right">State</th>
                <th className="text-right">Signals</th>
                <th className="text-right">Trades</th>
                <th className="text-right">P&L</th>
                <th className="text-right">PF</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <RunRow key={r.run_id} row={r}
                         active={activeRun === r.run_id}
                         onClick={() => onSelect(r.run_id)} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function RunRow({ row, active, onClick }) {
  const signals = row.total_signals ?? row.signals_so_far ?? 0;
  const trades = row.total_trades ?? row.trades_so_far ?? 0;
  const pnl = Number(row.gross_realized_pnl ?? row.pnl_so_far ?? 0);
  const pf = row.profit_factor?.toFixed?.(2) ?? "—";
  return (
    <tr className={`border-t border-slate-800 cursor-pointer hover:bg-slate-900/50 ${active ? "bg-slate-900/70" : ""}`}
        onClick={onClick}
        data-testid={`mtf-run-row-${row.run_id}`}>
      <td className="py-1.5 text-slate-400 truncate max-w-[140px]">{row.run_id.slice(-8)}</td>
      <td className="text-slate-400 truncate max-w-[100px]">{row.label || "—"}</td>
      <td className="text-slate-500">{row.from_date} → {row.to_date}</td>
      <td className={`text-right ${runStateTone(row.state)}`}>{row.state}</td>
      <td className="text-right text-slate-300">{signals}</td>
      <td className="text-right text-slate-300">{trades}</td>
      <td className={`text-right ${pnlTone(pnl)}`}>₹{pnl.toFixed(0)}</td>
      <td className="text-right text-slate-400">{pf}</td>
    </tr>
  );
}


// ===========================================================================
// Run detail — summary + charts + trades
// ===========================================================================
function RunResults({ summary, equity, trades, candles, selectedDate, onSelectDate }) {
  const equityChart = useMemo(() => equity.map((e) => ({
    date: e.trading_date,
    bees: Number(e.bees_book_cash || 0),
    options: Number(e.options_book_cash || 0),
    pnl: Number(e.realized_pnl || 0),
    trades: Number(e.trades || 0),
  })), [equity]);

  const priceChart = useMemo(() => {
    if (!candles?.candles) return [];
    return candles.candles.map((c) => ({
      ts: c.ts.slice(11, 16),
      full_ts: c.ts,
      close: c.c, high: c.h, low: c.l, open: c.o,
    }));
  }, [candles]);

  return (
    <>
      <SummaryCards summary={summary} />
      <EquityChart data={equityChart} />
      <PriceRegimeChart
        priceChart={priceChart} candles={candles}
        equity={equity} selectedDate={selectedDate}
        onSelectDate={onSelectDate}
      />
      <TradesTable trades={trades} summary={summary} />
    </>
  );
}

function SummaryCards({ summary }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-6 gap-2" data-testid="mtf-summary-cards">
      <SummaryCard label="State" value={summary.state || "—"} tone={summaryTone(summary.state)} />
      <SummaryCard label="Trades" value={String(summary.total_trades ?? 0)} />
      <SummaryCard label="Win rate" value={pctFmt(summary.win_rate)} tone="muted" />
      <SummaryCard label="Expectancy" value={inrFmt(summary.expectancy, 2)}
                    tone={Number(summary.expectancy) > 0 ? "pos" : "neg"} />
      <SummaryCard label="Profit factor" value={summary.profit_factor?.toFixed?.(2) ?? "—"} />
      <SummaryCard label="Max drawdown" value={inrFmt(summary.max_drawdown, 0)} tone="neg" />
    </div>
  );
}

function SummaryCard({ label, value, tone = "muted" }) {
  let cls = "text-slate-300";
  if (tone === "pos") cls = "text-pos";
  else if (tone === "neg") cls = "text-neg";
  else if (tone === "warn") cls = "text-warn";
  return (
    <div className="surface rounded-sm p-3">
      <div className="text-[9px] uppercase tracking-[0.2em] text-slate-500 mb-1">{label}</div>
      <div className={`text-sm font-mono ${cls}`}>{value}</div>
    </div>
  );
}

function EquityChart({ data }) {
  return (
    <div className="surface rounded-sm p-4" data-testid="mtf-equity-chart">
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">
        Per-Book Equity Curves
      </div>
      {data.length === 0 ? (
        <p className="text-xs text-slate-500 py-4">No equity data yet.</p>
      ) : (
        <div style={{ width: "100%", height: 240 }}>
          <ResponsiveContainer>
            <ComposedChart data={data} margin={CHART_MARGIN}>
              <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={AXIS_TICK} />
              <YAxis tick={AXIS_TICK} domain={EQUITY_Y_DOMAIN} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <ReferenceLine y={100000} stroke="#475569" strokeDasharray="4 4"
                              label={REF_LINE_LABEL} />
              <Line type="monotone" dataKey="bees" stroke="#22c55e"
                    strokeWidth={2} dot={false} name="NIFTYBEES book" />
              <Line type="monotone" dataKey="options" stroke="#94a3b8"
                    strokeWidth={1} strokeDasharray="4 4" dot={false}
                    name="Options (skipped)" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function PriceRegimeChart({ priceChart, candles, equity, selectedDate, onSelectDate }) {
  return (
    <div className="surface rounded-sm p-4" data-testid="mtf-price-chart">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
          NIFTY 1-min · Regime shading · Trade markers
        </div>
        <select value={selectedDate}
                 onChange={(e) => onSelectDate(e.target.value)}
                 data-testid="mtf-chart-date-select"
                 className="text-[10px] font-mono bg-slate-950 border border-slate-800 text-slate-200 px-2 py-1 rounded-sm">
          {equity.map((e) => (
            <option key={e.trading_date} value={e.trading_date}>{e.trading_date}</option>
          ))}
        </select>
      </div>
      {priceChart.length === 0 ? (
        <p className="text-xs text-slate-500 py-4">Pick a date with data.</p>
      ) : (
        <div style={{ width: "100%", height: 300 }}>
          <ResponsiveContainer>
            <ComposedChart data={priceChart} margin={CHART_MARGIN}>
              <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
              <XAxis dataKey="ts" tick={AXIS_TICK_SMALL} interval={30} />
              <YAxis tick={AXIS_TICK} domain={PRICE_Y_DOMAIN} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              {(candles?.regimes || []).map((r) => (
                <ReferenceArea key={regimeKey(r)}
                                x1={r.start.slice(11, 16)}
                                x2={r.end.slice(11, 16)}
                                fill={r.bullish ? REGIME_BULL_COLOR : REGIME_BEAR_COLOR}
                                fillOpacity={REGIME_FILL_OPACITY}
                                ifOverflow="hidden" />
              ))}
              {(candles?.trades || []).map((t) => (
                <TradeMarkers key={tradeMarkerKey(t)} trade={t} />
              ))}
              <Line type="monotone" dataKey="close" stroke="#c084fc"
                    strokeWidth={1.5} dot={false} name="NIFTY" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function TradeMarkers({ trade }) {
  return (
    <>
      {trade.entry_ts ? (
        <ReferenceLine x={tsLabel(trade.entry_ts)}
                        stroke="#22c55e" strokeDasharray="3 3"
                        label={ENTRY_MARKER_LABEL} />
      ) : null}
      {trade.exit_ts ? (
        <ReferenceLine x={tsLabel(trade.exit_ts)}
                        stroke={exitReasonStroke(trade.exit_reason)}
                        strokeDasharray="3 3"
                        label={{ ...EXIT_MARKER_LABEL_BASE,
                                  value: exitCode(trade.exit_reason) }} />
      ) : null}
    </>
  );
}

function TradesTable({ trades, summary }) {
  if (trades.length === 0) {
    const msg = summary.state === "COMPLETED"
      ? "No trades executed. Run had signals but every branch was rejected (spread / cost gate / regime)."
      : "Waiting for run to progress…";
    return (
      <div className="surface rounded-sm p-4" data-testid="mtf-trades-list">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">
          Trades (0)
        </div>
        <p className="text-xs text-slate-500 py-4">{msg}</p>
      </div>
    );
  }
  return (
    <div className="surface rounded-sm p-4" data-testid="mtf-trades-list">
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">
        Trades ({trades.length})
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px] font-mono">
          <thead className="text-[9px] uppercase tracking-[0.15em] text-slate-600">
            <tr>
              <th className="text-left py-1">Date</th>
              <th className="text-left">Entry</th>
              <th className="text-left">Exit</th>
              <th className="text-right">Qty</th>
              <th className="text-right">Entry Fill</th>
              <th className="text-right">Exit Fill</th>
              <th className="text-right">Reason</th>
              <th className="text-right">Gross</th>
              <th className="text-right">Charges</th>
              <th className="text-right">Realized</th>
            </tr>
          </thead>
          <tbody>
            {trades.map((t, i) => (
              <TradeRow key={tradeKey(t, i)} t={t} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TradeRow({ t }) {
  return (
    <tr className="border-t border-slate-800 hover:bg-slate-900/50">
      <td className="py-1.5 text-slate-400">{t.trading_date}</td>
      <td className="text-slate-400">{tsLabel(t.entry_ts)}</td>
      <td className="text-slate-400">{tsLabel(t.exit_ts)}</td>
      <td className="text-right text-slate-300">{t.quantity}</td>
      <td className="text-right text-slate-300">₹{Number(t.entry_fill_bees || 0).toFixed(2)}</td>
      <td className="text-right text-slate-300">₹{Number(t.exit_price_bees || 0).toFixed(2)}</td>
      <td className={`text-right ${exitReasonTone(t.exit_reason)}`}>{exitCode(t.exit_reason)}</td>
      <td className={`text-right ${pnlTone(t.gross_pnl)}`}>₹{Number(t.gross_pnl || 0).toFixed(2)}</td>
      <td className="text-right text-slate-500">₹{Number(t.charges_total || 0).toFixed(2)}</td>
      <td className={`text-right ${pnlTone(t.realized_pnl)}`}>₹{Number(t.realized_pnl || 0).toFixed(2)}</td>
    </tr>
  );
}
